import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { getWeeklyCard, type ReadingRecord } from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import TarotCard from '@/components/TarotCard';
import ErrorReportButton from '@/components/ErrorReportButton';
import PageNav from '@/components/PageNav';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Card of the week — Priestess-only.
 *
 * Free users see an upsell card explaining what the subscription unlocks
 * and a button to the upgrade flow. Subscribers see the actual reading.
 * The backend caches one card per (user, ISO week start) so the card
 * stays the same Mon→Sun.
 */
export default function Weekly() {
  const { t, i18n } = useTranslation();
  const { isPriestess } = useSubscription();

  const [reading, setReading] = useState<ReadingRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Raw error message kept separately — surfaced only via the report button
  // (the user sees a friendly localized line, the admin gets the real text).
  const [errorRaw, setErrorRaw] = useState<string>('');
  // Bumping `retryNonce` re-fires the fetch effect.
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!isPriestess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getWeeklyCard()
      .then(setReading)
      .catch((e) => {
        setError('load_failed');
        setErrorRaw((e as Error).message);
      })
      .finally(() => setLoading(false));
  }, [isPriestess, retryNonce]);

  // Weekly card's `for_date` is the Monday that opened the week. Build the
  // explicit Mon→Sun range so the user sees "Active from X to Y" instead
  // of guessing what "until next Monday" means in their timezone.
  const weekRange = (() => {
    if (!reading?.for_date) return null;
    const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';
    const from = new Date(`${reading.for_date}T12:00:00Z`);
    const to = new Date(from);
    to.setUTCDate(to.getUTCDate() + 6);
    const fmt = (d: Date) =>
      d.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    return { from: fmt(from), to: fmt(to) };
  })();

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        ✦ {t('weekly.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('weekly.subtitle')}
      </p>

      {!isPriestess && (
        <OrnateFrame className="card-glow border-gold-500/50">
          <div className="text-center">
            <p className="mb-2 text-[10px] uppercase tracking-[0.25em] text-gold-500/70">
              ✦ ✦ ✦
            </p>
            <h2 className="mb-3 font-serif text-xl text-gold-200">
              {t('weekly.locked_title')}
            </h2>
            <p className="mb-5 font-serif text-sm italic leading-relaxed text-gold-300/80">
              {t('weekly.locked_blurb')}
            </p>
            <Link to="/profile" className="btn-primary block w-full text-center">
              {t('weekly.upgrade_cta')}
            </Link>
          </div>
        </OrnateFrame>
      )}

      {isPriestess && loading && (
        <p className="animate-pulse text-center text-gold-300/70">
          {t('reading.loading')}
        </p>
      )}

      {isPriestess && error && (
        <OrnateFrame className="card-glow border-rose-400/30 text-center">
          <p className="mb-4 font-serif text-sm italic leading-relaxed text-gold-200">
            {t('weekly.load_failed')}
          </p>
          <button
            type="button"
            onClick={() => setRetryNonce((n) => n + 1)}
            className="btn-primary mb-3"
          >
            ↻ {t('weekly.retry')}
          </button>
          <div className="mt-2">
            <ErrorReportButton
              screen="weekly"
              message={errorRaw || 'Failed to fetch'}
              context={{ language: i18n.language }}
            />
          </div>
        </OrnateFrame>
      )}

      {isPriestess && reading && (
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="flex justify-center">
            <motion.div
              initial={{ rotateY: 180, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            >
              <TarotCard card={reading.cards[0]} size={200} />
            </motion.div>
          </div>

          <OrnateFrame className="card-glow">
            <RichText
              text={reading.interpretation}
              className="font-serif italic leading-relaxed text-gold-200"
            />
          </OrnateFrame>

          {weekRange && (
            <p className="text-center text-[11px] uppercase tracking-widest text-gold-500/60">
              ✦ {t('weekly.active_range', { from: weekRange.from, to: weekRange.to })}
            </p>
          )}
        </motion.section>
      )}

      <PageNav />
    </main>
  );
}
